import React from 'react';
import './DataTable.css';

/**
 * Responsive data table.
 *
 * A table is a desktop affordance. Below 641px the same rows render as a list
 * of opaque record cards — opaque because Apple reserves Liquid Glass for the
 * navigation layer, and a glass table would compete with the bars.
 *
 * Each column declares its priority once and both renderings follow:
 *   primary  → the card's title            (exactly one)
 *   summary  → shown on the card face      (2-4 recommended)
 *   detail   → behind the card's disclosure
 *
 * Nothing is hidden: every column stays reachable on a phone. The disclosure
 * changes how much arrives at once, not what exists — which matters for
 * payroll figures that have to stay auditable.
 */
export type ColumnPriority = 'primary' | 'summary' | 'detail';

export interface Column<T> {
  /** Stable key, used for React keys and nothing else. */
  key: string;
  /** Column heading, and the field label on a card. */
  header: string;
  priority: ColumnPriority;
  /** Right-aligns and applies tabular figures, so money lines up. */
  numeric?: boolean;
  /** index is the row's position, for grids edited by position. */
  render: (row: T, index: number) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string | number;
  /** Class applied to <tr> and to the card, for row-level state. */
  rowClass?: (row: T) => string | undefined;
  /** Shown when there are no rows. */
  empty?: React.ReactNode;
  /** Label for the disclosure. */
  moreLabel?: string;
  caption?: string;
  /**
   * Totals. Rendered as <tfoot> rows on desktop and as a distinct card below
   * the records on a phone — totals are separate from the rows either way, so
   * they get their own shape rather than being squeezed into a card face.
   */
  footer?: React.ReactNode;
  footerCard?: React.ReactNode;
  /**
   * Row action. Last column on desktop; a full-width control at the foot of
   * the card on a phone, where a 44px target in a 2-column grid would be
   * cramped and easy to mis-tap.
   */
  actions?: (row: T, index: number) => React.ReactNode;
}

const PHONE = '(max-width: 640px)';

function useIsPhone(): boolean {
  const [isPhone, setIsPhone] = React.useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia(PHONE).matches
  );
  React.useEffect(() => {
    const mq = window.matchMedia(PHONE);
    const onChange = (e: MediaQueryListEvent) => setIsPhone(e.matches);
    mq.addEventListener('change', onChange);
    setIsPhone(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isPhone;
}

export default function DataTable<T>({
  columns, rows, rowKey, rowClass, empty, moreLabel = 'More', caption,
  footer, footerCard, actions,
}: DataTableProps<T>) {
  const isPhone = useIsPhone();

  if (rows.length === 0) {
    return <div className="cs-table-empty">{empty ?? 'Nothing to show.'}</div>;
  }

  if (!isPhone) {
    return (
      <table className="cs-table">
        {caption && <caption>{caption}</caption>}
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.numeric ? 'num' : undefined}>{c.header}</th>
            ))}
            {actions && <th aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={rowKey(row, i)} className={rowClass?.(row)}>
              {columns.map((c) => (
                <td key={c.key} className={c.numeric ? 'num' : undefined}>{c.render(row, i)}</td>
              ))}
              {actions && <td>{actions(row, i)}</td>}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </table>
    );
  }

  const primary = columns.find((c) => c.priority === 'primary');
  const summary = columns.filter((c) => c.priority === 'summary');
  const detail = columns.filter((c) => c.priority === 'detail');

  return (
    <>
    <ul className="cs-records" aria-label={caption}>
      {rows.map((row, i) => (
        <li key={rowKey(row, i)} className={`cs-record ${rowClass?.(row) ?? ''}`}>
          {primary && <div className="cs-record__title">{primary.render(row, i)}</div>}

          {summary.length > 0 && (
            <dl className="cs-record__fields">
              {summary.map((c) => (
                <div className="cs-record__field" key={c.key}>
                  <dt>{c.header}</dt>
                  <dd className={c.numeric ? 'num' : undefined}>{c.render(row, i)}</dd>
                </div>
              ))}
            </dl>
          )}

          {actions && <div className="cs-record__actions">{actions(row, i)}</div>}

          {detail.length > 0 && (
            <details className="cs-record__more">
              <summary>{moreLabel}</summary>
              <dl className="cs-record__fields">
                {detail.map((c) => (
                  <div className="cs-record__field" key={c.key}>
                    <dt>{c.header}</dt>
                    <dd className={c.numeric ? 'num' : undefined}>{c.render(row, i)}</dd>
                  </div>
                ))}
              </dl>
            </details>
          )}
        </li>
      ))}
    </ul>
    {footerCard && <div className="cs-record cs-record--totals">{footerCard}</div>}
    </>
  );
}
